import { fromCents, toCents } from './money.js';

/**
 * Projects: the business's own lines of work, each with its own dashboard and
 * its own calendar. The three are fixed — they are how this organisation is
 * run, not something an administrator defines — so they are constants rather
 * than a table.
 */
export const PROJECT_KEYS = ['PHOTOBOOTH_GOA', 'WEDDINGS_DE_GOA', 'SOCIAL_MEDIA'] as const;
export type ProjectKey = (typeof PROJECT_KEYS)[number];

export interface ProjectInfo {
  key: ProjectKey;
  /** The URL segment: `/projects/photobooth-goa`. */
  slug: string;
  label: string;
}

export const PROJECTS: readonly ProjectInfo[] = [
  { key: 'PHOTOBOOTH_GOA', slug: 'photobooth-goa', label: 'Photobooth Goa' },
  { key: 'WEDDINGS_DE_GOA', slug: 'weddings-de-goa', label: 'Weddings de Goa' },
  { key: 'SOCIAL_MEDIA', slug: 'social-media-management', label: 'Social Media Management' },
];

export function projectBySlug(slug: string): ProjectInfo | undefined {
  return PROJECTS.find((project) => project.slug === slug);
}

export function projectByKey(key: ProjectKey): ProjectInfo {
  return PROJECTS.find((project) => project.key === key)!;
}

/** What a calendar entry is. Only the project it was added under ever shows it. */
export const PROJECT_EVENT_KINDS = ['EVENT_BOOKING', 'CONTENT_SHOOT', 'GENERAL'] as const;
export type ProjectEventKind = (typeof PROJECT_EVENT_KINDS)[number];

export const PROJECT_EVENT_KIND_LABELS: Record<ProjectEventKind, string> = {
  EVENT_BOOKING: 'Event booking',
  CONTENT_SHOOT: 'Content shoot',
  GENERAL: 'Reminder / date',
};

export const PROJECT_EVENT_STATUSES = ['SCHEDULED', 'COMPLETED', 'CANCELLED'] as const;
export type ProjectEventStatus = (typeof PROJECT_EVENT_STATUSES)[number];

/** Where a shot piece of content is in editing. Only content shoots carry one. */
export const EDITING_STATUSES = ['NOT_STARTED', 'IN_PROGRESS', 'DONE'] as const;
export type EditingStatus = (typeof EDITING_STATUSES)[number];

export const EDITING_STATUS_LABELS: Record<EditingStatus, string> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'Editing',
  DONE: 'Done',
};

/**
 * Which kinds each project can add. `GENERAL` (a marked date) is no longer
 * offered anywhere; the value remains so older entries still read.
 */
export const PROJECT_EVENT_KINDS_BY_PROJECT: Record<ProjectKey, readonly ProjectEventKind[]> = {
  PHOTOBOOTH_GOA: ['EVENT_BOOKING'],
  WEDDINGS_DE_GOA: [],
  SOCIAL_MEDIA: ['CONTENT_SHOOT'],
};

// ---------------------------------------------------------------------------
// Photobooth event bookings
// ---------------------------------------------------------------------------

/** Where a booking stands. Colours the calendar and drives the dashboard counts. */
export const BOOKING_STATUSES = [
  'IN_DISCUSSION',
  'CONFIRMED',
  'COMPLETE',
  'CANCELLED',
  'MARKETING',
  'MISTAKE',
] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  IN_DISCUSSION: 'In discussion',
  CONFIRMED: 'Confirmed',
  COMPLETE: 'Complete',
  CANCELLED: 'Cancelled',
  MARKETING: 'Marketing',
  MISTAKE: 'Mistake',
};

export const BOOTH_TYPES = [
  'Digital mirror New',
  'Digital mirror old',
  'Retro Booth old',
  'Retro booth new',
  '360 Video booth',
  'Magazine booth',
  'Classic booth',
  'Audio guest book',
  'Premium video booth',
  'High angle booth',
  'Royal oval booth',
  'Keychain booth',
  'Magnet booth',
  'AI booth',
  'DSLR booth',
] as const;

export const EVENT_TYPES = [
  'Goan wedding',
  'Birthday',
  'Anniversary',
  'Destination wedding',
  'Corporate',
  'Private party',
] as const;

export const INQUIRY_SOURCES = [
  'Whatsapp',
  'Instagram',
  'Email',
  'Website',
  'Friend referral',
] as const;

/**
 * The legacy event status that goes with a booking status, so the reminders
 * and anything else that reads `status` keep working: a cancelled or mistaken
 * booking is off the calendar's agenda, a complete one is done.
 */
export function legacyStatusFor(status: BookingStatus): ProjectEventStatus {
  if (status === 'COMPLETE') return 'COMPLETED';
  if (status === 'CANCELLED' || status === 'MISTAKE') return 'CANCELLED';
  return 'SCHEDULED';
}

/** The dashboard's "up or down against last month" figure. */
export interface Trend {
  direction: 'up' | 'down' | 'flat';
  /** Whole percent, or null when last month was zero so there is nothing to compare to. */
  percent: number | null;
}

export function trendOf(current: string | number, previous: string | number): Trend {
  const now = Number(current);
  const before = Number(previous);
  if (now === before) return { direction: 'flat', percent: before === 0 ? null : 0 };
  const direction = now > before ? 'up' : 'down';
  if (before === 0) return { direction, percent: null };
  return { direction, percent: Math.round((Math.abs(now - before) / Math.abs(before)) * 100) };
}

// ---------------------------------------------------------------------------
// The photobooth event expense
// ---------------------------------------------------------------------------

export const EXPENSE_CURRENCIES = ['INR', 'USD'] as const;
export const EXPENSE_OWNERS = ['Photoboothgoa'] as const;
export const ADDITIONAL_EXPENSE_NAMES = ['Fuel', 'Food', 'Supplies'] as const;
export const ACCESSORY_ITEMS = [
  'Fridge magnets',
  'Keychains',
  'Paper frame',
  'Wooden frame',
] as const;
export const VEHICLES = [
  'Office car',
  'Vishwajit pickup',
  'Vishwajit car',
  'Hanumant pickup',
  'Agnel Eeco',
  'Omkar car',
] as const;

export const TAX_RATE_GST = 18;
export const TAX_RATE_NON_GST = 10;
export const LEAD_RATE = 8.5;
export const ADMIN_SALES_RATE = 3.5;
export const DEFAULT_ASSIST_AMOUNT = '1000';

export interface PhotoboothExpenseInput {
  gstInvoice: boolean;
  invoiceAmount: string | number;
  plannerCommission?: string | number;
  accessoriesAmount?: string | number;
  assist1Amount?: string | number;
  assist2Amount?: string | number;
  transportationCost?: string | number;
}

export interface PhotoboothExpenseTotals {
  /** 18 for a GST invoice, 10 otherwise. */
  taxRate: number;
  taxAmount: string;
  /** The invoice less planner commission and accessories, less the tax on that. */
  newAmount: string;
  leadAmount: string;
  adminSalesAmount: string;
  totalExpense: string;
  totalProfit: string;
}

/** `cents × percent`, to the nearest cent. Percent carries at most two decimals. */
function percentOf(cents: bigint, percent: number): bigint {
  const basisPoints = BigInt(Math.round(percent * 100));
  const product = cents * basisPoints;
  const half = product >= 0n ? 5000n : -5000n;
  return (product + half) / 10000n;
}

/**
 * The numbers on an event expense, worked out the way the business does them:
 *
 *  - take the invoice, less the planner's commission and the accessories;
 *  - take 18% (GST invoice) or 10% (otherwise) off what is left — that is the
 *    new amount;
 *  - lead gets 8.5% and admin sales 3.5% of the new amount;
 *  - total expense is lead + admin sales + both assists/driver + transportation;
 *  - profit is the new amount less that total expense.
 *
 * One function, used by the form as you type and by the server on save, so the
 * figure on screen is the figure stored. Integer cents throughout.
 */
export function computePhotoboothExpense(input: PhotoboothExpenseInput): PhotoboothExpenseTotals {
  const invoice = toCents(input.invoiceAmount || 0);
  const planner = toCents(input.plannerCommission || 0);
  const accessories = toCents(input.accessoriesAmount || 0);
  const assist1 = toCents(input.assist1Amount ?? DEFAULT_ASSIST_AMOUNT);
  const assist2 = toCents(input.assist2Amount ?? DEFAULT_ASSIST_AMOUNT);
  const transport = toCents(input.transportationCost || 0);

  const taxRate = input.gstInvoice ? TAX_RATE_GST : TAX_RATE_NON_GST;
  const base = invoice - planner - accessories;
  const tax = percentOf(base, taxRate);
  const newAmount = base - tax;

  const lead = percentOf(newAmount, LEAD_RATE);
  const adminSales = percentOf(newAmount, ADMIN_SALES_RATE);
  const totalExpense = lead + adminSales + assist1 + assist2 + transport;

  return {
    taxRate,
    taxAmount: fromCents(tax),
    newAmount: fromCents(newAmount),
    leadAmount: fromCents(lead),
    adminSalesAmount: fromCents(adminSales),
    totalExpense: fromCents(totalExpense),
    totalProfit: fromCents(newAmount - totalExpense),
  };
}
